import { pbClient } from './pbClient'
import { resolveCollectionName } from './collectionMapping'
import { runtimeEnv } from './runtimeEnv'

const ERROR_COLLECTION_KEY = 'error_logs'
const PERFORMANCE_COLLECTION_KEY = 'performance_metrics'
const DEFAULT_FLUSH_INTERVAL_MS = 15000
const MAX_BUFFER_SIZE = 100

interface TelemetryErrorPayload {
  user?: string
  level: 'error' | 'warn'
  message: string
  code?: string
  stack?: string
  source: string
  context?: string
  metadata?: string
  timestamp: string
  appVersion: string
  environment: string
}

class TelemetryService {
  private buffer: TelemetryErrorPayload[] = []
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private isFlushing = false
  private initialized = false
  private active = true
  private collectionName = resolveCollectionName(ERROR_COLLECTION_KEY)
  private performanceCollectionName = resolveCollectionName(PERFORMANCE_COLLECTION_KEY)

  initialize(): void {
    if (this.initialized) return
    if (!runtimeEnv.isFeatureEnabled('errorTracking')) return

    this.initialized = true
    this.collectionName = resolveCollectionName(ERROR_COLLECTION_KEY)
    this.performanceCollectionName = resolveCollectionName(PERFORMANCE_COLLECTION_KEY)

    if (typeof window !== 'undefined') {
      window.addEventListener('error', this.onWindowError)
      window.addEventListener('unhandledrejection', this.onUnhandledRejection)
    }

    this.flushTimer = setInterval(() => {
      void this.flush()
    }, DEFAULT_FLUSH_INTERVAL_MS)
  }

  shutdown(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('error', this.onWindowError)
      window.removeEventListener('unhandledrejection', this.onUnhandledRejection)
    }

    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }

    void this.flush()
  }

  trackError(error: unknown, source = 'app', context?: Record<string, unknown>): void {
    if (!runtimeEnv.isFeatureEnabled('errorTracking')) return

    const payload: TelemetryErrorPayload = {
      user: pbClient.getAuthUser()?.id,
      level: 'error',
      message: this.getErrorMessage(error),
      code: this.getErrorCode(error),
      stack: this.getErrorStack(error),
      source,
      context: context ? this.safeSerialize(context) : undefined,
      metadata: undefined,
      timestamp: new Date().toISOString(),
      appVersion: runtimeEnv.getEnvironment().appVersion,
      environment: runtimeEnv.getEnvironment().appEnv,
    }

    this.push(payload)
  }

  trackWarning(message: string, source = 'app', metadata?: Record<string, unknown>): void {
    if (!runtimeEnv.isFeatureEnabled('errorTracking')) return

    const payload: TelemetryErrorPayload = {
      user: pbClient.getAuthUser()?.id,
      level: 'warn',
      message,
      source,
      metadata: metadata ? this.safeSerialize(metadata) : undefined,
      timestamp: new Date().toISOString(),
      appVersion: runtimeEnv.getEnvironment().appVersion,
      environment: runtimeEnv.getEnvironment().appEnv,
    }

    this.push(payload)
  }

  async flush(): Promise<void> {
    if (!this.initialized || !this.active || this.isFlushing || this.buffer.length === 0) return

    this.isFlushing = true

    try {
      const batch = this.buffer.splice(0, this.buffer.length)
      await Promise.all(
        batch.map(async (payload) => {
          try {
            await pbClient.createRecord(this.collectionName, payload as unknown as Record<string, unknown>)
          } catch (error) {
            if (this.isPermanentCollectionFailure(error)) {
              this.disableChannel(this.describeFailure(error))
              return
            }

            // Transient issue: requeue and continue fail-open.
            this.buffer.push(payload)
          }
        })
      )
    } finally {
      this.isFlushing = false
    }
  }

  getBufferSize(): number {
    return this.buffer.length
  }

  private push(payload: TelemetryErrorPayload): void {
    if (!this.active) return

    this.buffer.push(payload)

    if (this.buffer.length > MAX_BUFFER_SIZE) {
      this.buffer.shift()
    }

    if (navigator.onLine) {
      void this.flush()
    }
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message
    return String(error)
  }

  private getErrorCode(error: unknown): string | undefined {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      return String((error as { code: unknown }).code)
    }
    return undefined
  }

  private getErrorStack(error: unknown): string | undefined {
    if (error instanceof Error && error.stack) return error.stack
    return undefined
  }

  private safeSerialize(input: unknown): string {
    try {
      return JSON.stringify(input)
    } catch {
      return '{"error":"serialize_failed"}'
    }
  }

  private onWindowError = (event: ErrorEvent): void => {
    if (this.isTelemetryRequestError(event)) return

    this.trackError(event.error || event.message, 'window.error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  }

  private onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (this.isLikelyTelemetryRejection(event.reason)) return
    this.trackError(event.reason, 'window.unhandledrejection')
  }

  private isPermanentCollectionFailure(error: unknown): boolean {
    const statusCode = this.getStatusCode(error)
    return statusCode === 403 || statusCode === 404
  }

  private getStatusCode(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) return undefined
    if ('statusCode' in error) {
      return Number((error as { statusCode?: unknown }).statusCode)
    }
    return undefined
  }

  private describeFailure(error: unknown): string {
    const status = this.getStatusCode(error)
    if (status === 404) {
      return `Collection '${this.collectionName}' not found`
    }
    if (status === 403) {
      return `Collection '${this.collectionName}' forbidden by rules`
    }
    return `Telemetry disabled due to persistent failure on '${this.collectionName}'`
  }

  private disableChannel(reason: string): void {
    if (!this.active) return
    this.active = false
    this.buffer = []

    if (runtimeEnv.shouldLog('warn')) {
      console.warn('[Telemetry] Disabled error tracking channel:', reason)
    }
  }

  private isTelemetryRequestError(event: ErrorEvent): boolean {
    const file = String(event.filename || '')
    const message = String(event.message || '')

    const errorPath = `/api/collections/${this.collectionName}/records`
    const perfPath = `/api/collections/${this.performanceCollectionName}/records`

    return (
      file.includes(errorPath) ||
      file.includes(perfPath) ||
      message.includes(errorPath) ||
      message.includes(perfPath)
    )
  }

  private isLikelyTelemetryRejection(reason: unknown): boolean {
    const errorPath = `/api/collections/${this.collectionName}/records`
    const perfPath = `/api/collections/${this.performanceCollectionName}/records`

    if (typeof reason === 'string') {
      return reason.includes(errorPath) || reason.includes(perfPath)
    }

    if (typeof reason === 'object' && reason !== null && 'message' in reason) {
      const message = String((reason as { message?: unknown }).message)
      return (
        message.includes(errorPath) ||
        message.includes(perfPath)
      )
    }

    return false
  }
}

export const telemetry = new TelemetryService()
export default telemetry
